import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import Checkbox from '../../forms/Checkbox';
import { useForm, useWatch } from 'react-hook-form';
import SelectMenu from '../../forms/SelectMenu';
import { useEffect } from 'react';
import * as clientSlice from '../../../data/client.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { RideshareClientAdminSetting } from '../../../data/models/client-admin-settings.model';
import { RideshareProvider as RideShareBC } from '../../../data/models/provider-call-outcome.model';

interface ClientAdministrationProps {
  clientBC?: string;
  open: boolean;
  handleClose: () => void;
}

const PREFIX = 'ClientAdministration';

const classes = {
  formContainer: `${PREFIX}-formContainer`,
  actions: `${PREFIX}-actions`,
  actionButton: `${PREFIX}-actionButton`,
  closeButton: `${PREFIX}-closeButton`,
  rideshareHeader: `${PREFIX}-rideshareHeader`,
  prioritySelect: `${PREFIX}-prioritySelect`,
  closeButtonContainer: `${PREFIX}-closeButtonContainer`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: 444,
  },
  '& .MuiFormControlLabel-label': {
    ...theme.typography.body1,
  },
  [`& .${classes.formContainer}`]: {
    paddingLeft: theme.spacing(1),
    gap: theme.spacing(1),
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'end',
    paddingBottom: theme.spacing(5),
  },
  [`& .${classes.actionButton}`]: {
    fontSize: 15,
    height: 48,
  },
  [`& .${classes.closeButton}`]: {
    width: 180,
    flex: 'none',
  },
  [`& .${classes.rideshareHeader}`]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.prioritySelect}`]: {
    marginLeft: 34,
    width: 110,
  },
  [`& .${classes.closeButtonContainer}`]: {
    width: '100%',
    display: 'flex',
    justifyContent: 'end',
  },
}));

interface RideShareFormData {
  enabled: boolean;
  priority?: number;
}

interface ClientAdminFormData {
  rideshare: {
    uber: RideShareFormData;
    lyft: RideShareFormData;
  };
}

type RideShareFormMapOptional = {
  [K in keyof ClientAdminFormData['rideshare']]?: RideShareFormData;
};

export default function ClientAdministration({
  clientBC,
  open,
  handleClose,
}: ClientAdministrationProps) {
  const readOnly = useIsSuperReaderUser();
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const clientAdminSettings = useAppSelector(
    clientSlice.selectClientAdminSettings,
  );

  const formHook = useForm<ClientAdminFormData>({
    defaultValues: {
      rideshare: {
        uber: {
          enabled: false,
        },
        lyft: {
          enabled: false,
        },
      },
    },
  });

  type RideshareProvider = keyof ClientAdminFormData['rideshare'];

  const rideshareProviders: RideshareProvider[] = ['uber', 'lyft'];
  const priorities = rideshareProviders.map((provider, index) => ({
    name: index + 1,
    value: index + 1,
  }));

  const rideshareValues =
    useWatch({
      control: formHook.control,
      name: 'rideshare',
    }) || {};

  let isFormValid = true;

  Object.values(rideshareValues).forEach((value) => {
    if (value.enabled && !value.priority) {
      isFormValid = false;
    }
  });

  useEffect(() => {
    if (clientBC && open) {
      dispatch(clientSlice.fetchClientAdminSettingsAsync(clientBC));
    }
  }, [clientBC, open]);

  useEffect(() => {
    if (
      clientAdminSettings &&
      clientAdminSettings.clientBC === clientBC &&
      clientAdminSettings.rideshare
    ) {
      const rideshareFormData: RideShareFormMapOptional = {};
      const rideshareSettings = clientAdminSettings.rideshare;
      rideshareProviders.forEach((provider) => {
        const matchingProvider = rideshareSettings.find(
          (rideshare) => rideshare.rideshareBC.toLowerCase() === provider,
        );
        if (matchingProvider) {
          rideshareFormData[provider] = {
            enabled: true,
            priority: matchingProvider.priority,
          };
        } else {
          rideshareFormData[provider] = {
            enabled: false,
          };
        }
        formHook.setValue('rideshare', rideshareFormData as any);
      });
    }
  }, [clientAdminSettings, clientBC]);

  useEffect(() => {
    // Reset priority if rideshare provider is disabled
    Object.entries(rideshareValues).forEach(([key, value]) => {
      if (!value.enabled && value.priority) {
        const rideshareProvider = key as RideshareProvider;
        formHook.setValue(`rideshare.${rideshareProvider}.priority`, undefined);
      }
    });
  }, [rideshareValues]);

  const handleCancel = () => {
    formHook.reset();
    handleClose();
  };

  const handleSave = (data: ClientAdminFormData) => {
    if (!clientBC) {
      return;
    }
    const rideshare: RideshareClientAdminSetting[] = Object.entries(
      data.rideshare,
    )
      .filter(([providerName, value]) => {
        return value.enabled;
      })
      .map(([providerName, value]) => ({
        rideshareBC: providerName.toUpperCase() as RideShareBC,
        priority: value.priority!,
      }));

    dispatch(
      clientSlice.updateClientAdminSettingsAsync({
        clientBC,
        rideshare,
      }),
    );
  };

  return (
    <StyledDialog open={open}>
      <DialogTitle>{t('clientAdministration.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="h5" className={classes.rideshareHeader}>
          {t('clientAdministration.rideshareManagement')}
        </Typography>
        <Stack direction="column" className={classes.formContainer}>
          {rideshareProviders.map((provider) => {
            const isEnabled = rideshareValues?.[provider]?.enabled;

            return (
              <Stack direction="column" key={provider}>
                <Checkbox
                  control={formHook.control}
                  label={t(`clientAdministration.${provider}`)}
                  name={`rideshare.${provider}.enabled`}
                  readOnly={readOnly}
                />
                {isEnabled && (
                  <SelectMenu
                    className={classes.prioritySelect}
                    label={t('clientAdministration.priority')}
                    name={`rideshare.${provider}.priority`}
                    values={priorities}
                    valueAttr="value"
                    labelAttr="name"
                    required={true}
                    control={formHook.control}
                    readOnly={readOnly}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        {readOnly && (
          <Box className={classes.closeButtonContainer}>
            <Button
              variant="contained"
              color="primary"
              className={`${classes.actionButton} ${classes.closeButton}`}
              onClick={handleCancel}>
              {t('common.close')}
            </Button>
          </Box>
        )}
        {!readOnly && (
          <>
            <Button
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.actionButton}
              disabled={!isFormValid}
              onClick={formHook.handleSubmit(handleSave)}>
              {t('common.save')}
            </Button>
          </>
        )}
      </DialogActions>
    </StyledDialog>
  );
}
